/**
 * TTS routing layer.
 *
 * OpenAI  → English + European languages → returns MP3 (audio/mpeg)
 * Sarvam  → Indian languages             → returns WAV (audio/wav)
 *
 * In dev:  calls providers directly using VITE_* API keys.
 * In prod: routes through /api/tts (OpenAI) or /api/sarvam-tts (Sarvam)
 *          so keys stay server-side.
 */

// ── Low-level fetch helpers ───────────────────────────────────────────────────

async function fetchOpenAI(
  text: string,
  voice: string,
  model: string,
  signal: AbortSignal,
): Promise<ArrayBuffer> {
  let response: Response
  if (import.meta.env.DEV) {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY
    if (!apiKey) throw new Error('VITE_OPENAI_API_KEY not set in .env.local')
    response = await fetch('https://api.openai.com/v1/audio/speech', {
      method: 'POST',
      headers: { Authorization: `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ model, input: text, voice }),
      signal,
    })
  } else {
    response = await fetch('/api/tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, voice, model }),
      signal,
    })
  }
  if (!response.ok) {
    let detail = ''
    try { detail = (await response.text()).slice(0, 400) } catch { /* ignore */ }
    throw new Error(`OpenAI TTS ${response.status}: ${detail || response.statusText}`)
  }
  return response.arrayBuffer()
}

async function fetchSarvam(
  text: string,
  voice: string,
  model: string,
  langCode: string,
  signal: AbortSignal,
): Promise<ArrayBuffer> {
  let response: Response
  if (import.meta.env.DEV) {
    const apiKey = import.meta.env.VITE_SARVAM_API_KEY
    if (!apiKey) throw new Error('VITE_SARVAM_API_KEY not set in .env.local')
    response = await fetch('https://api.sarvam.ai/text-to-speech', {
      method: 'POST',
      headers: {
        'api-subscription-key': apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        target_language_code: langCode,
        speaker: voice,
        model,
      }),
      signal,
    })
  } else {
    response = await fetch('/api/sarvam-tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, voice, model, langCode }),
      signal,
    })
  }
  if (!response.ok) {
    // Surface Sarvam's actual error body (which says exactly which speaker /
    // model / language code is invalid) instead of just the status text.
    let detail = ''
    try { detail = (await response.text()).slice(0, 400) } catch { /* ignore */ }
    throw new Error(`Sarvam TTS ${response.status}: ${detail || response.statusText}`)
  }

  if (import.meta.env.DEV) {
    // bulbul:v3 may return raw WAV bytes directly or the JSON { audios: [base64wav] } envelope.
    // Content-type isn't always reliable, so clone first and try JSON; fall back to raw bytes.
    const ct = response.headers.get('content-type') ?? ''
    if (ct.includes('audio/')) {
      return response.arrayBuffer()
    }
    const clone = response.clone()
    try {
      const json = await response.json() as { audios: string[]; request_id?: string }
      if (!json.audios?.[0]) throw new Error('Sarvam returned no audio')
      const b64 = json.audios[0]
      const binary = atob(b64)
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
      return bytes.buffer
    } catch (e) {
      if (e instanceof Error && e.message === 'Sarvam returned no audio') throw e
      // Body wasn't JSON — treat as raw audio bytes
      return clone.arrayBuffer()
    }
  } else {
    // Proxy /api/sarvam-tts already decoded base64 → returns raw WAV bytes
    return response.arrayBuffer()
  }
}

// ── Error mapping ─────────────────────────────────────────────────────────────

/**
 * Maps a known TTS-provider failure to a learner-safe, human-readable message.
 * Returns `null` when the error isn't a recognised TTS failure, so callers can
 * apply their own context-specific fallback. Never returns raw provider JSON.
 *
 * The fetch helpers above throw `… TTS <status>: <body>`, so we match on the
 * status / provider error codes embedded in that message. Currently handles the
 * out-of-credits case (Sarvam 402 `insufficient_quota_error`, OpenAI billing).
 */
export function friendlyTtsError(err: unknown): string | null {
  const msg = err instanceof Error ? err.message : String(err)
  if (msg.includes('402') || msg.includes('insufficient_quota') || msg.includes('No credits'))
    return 'Narration service is temporarily out of credits — your saved audio is unaffected.'
  return null
}

// ── Web Audio helpers ─────────────────────────────────────────────────────────

let _audioCtx: AudioContext | null = null

/** Returns the shared AudioContext, creating or resuming it as needed. */
export function getAudioCtx(): AudioContext {
  if (!_audioCtx || _audioCtx.state === 'closed') _audioCtx = new AudioContext()
  if (_audioCtx.state === 'suspended') void _audioCtx.resume()
  return _audioCtx
}

/** Pause the shared AudioContext — freezes any AudioBufferSourceNode
 *  mid-playback (used by Group A narrative formats via
 *  playPreloadedBuffer). Counterpart to resumeAudioCtx. No-op if the
 *  context doesn't exist or isn't currently running. */
export function pauseAudioCtx(): void {
  if (_audioCtx && _audioCtx.state === 'running') void _audioCtx.suspend()
}

/** Resume the shared AudioContext if it was previously suspended via
 *  pauseAudioCtx. No-op if the context doesn't exist or isn't suspended. */
export function resumeAudioCtx(): void {
  if (_audioCtx && _audioCtx.state === 'suspended') void _audioCtx.resume()
}

/** True iff the shared AudioContext exists and is currently running.
 *  Used by pause-button logic to detect Web-Audio-path playback when
 *  no HTMLAudioElement is active. */
export function isAudioCtxRunning(): boolean {
  return !!_audioCtx && _audioCtx.state === 'running'
}

/**
 * Trim leading silence from a decoded AudioBuffer.
 * OpenAI TTS reliably has 50–300 ms of silence at the start of every clip.
 */
function trimLeadingSilence(buf: AudioBuffer, threshold = 0.005): AudioBuffer {
  const data = buf.getChannelData(0)
  let startSample = 0
  for (let i = 0; i < data.length; i++) {
    if (Math.abs(data[i]) > threshold) {
      // Keep 10 ms before first audible sample so we don't clip the attack
      startSample = Math.max(0, i - Math.floor(buf.sampleRate * 0.01))
      break
    }
  }
  if (startSample === 0) return buf
  const ctx = getAudioCtx()
  const out = ctx.createBuffer(buf.numberOfChannels, buf.length - startSample, buf.sampleRate)
  for (let c = 0; c < buf.numberOfChannels; c++)
    out.copyToChannel(buf.getChannelData(c).subarray(startSample), c)
  return out
}

// ── Shared helpers ────────────────────────────────────────────────────────────

/** Encode an ArrayBuffer to base64 in chunks (avoids call-stack overflow). */
function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const CHUNK = 8192
  for (let i = 0; i < bytes.length; i += CHUNK)
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + CHUNK)))
  return btoa(binary)
}

/** Decode base64 → Blob with the correct MIME type. */
function base64ToBlob(b64: string, mimeType: string): Blob {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mimeType })
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Call the TTS API and return base64-encoded audio. No playback.
 *  Pass `language` to select the correct provider + voice; `gender`
 *  picks the male/female speaker within that language.
 *
 *  `voiceSurface` picks the voice catalog: 'narration' (course narration —
 *  prefers narrationVoices → Sarvam Indian accent for en-IN + Indian
 *  languages) vs 'dialogue' (Role Play — always the `voices` field,
 *  OpenAI for realtime languages). Defaults to 'dialogue' so Role Play
 *  call sites are untouched by the 2026-07-17 narration-accent fix;
 *  every Content Creator call site passes 'narration' explicitly.
 *  Callers playing the result must read mimeType from the SAME config fn. */
export async function generateAudioB64(
  text: string,
  signal?: AbortSignal,
  language = 'en',
  gender: 'female' | 'male' = 'female',
  voiceSurface: 'narration' | 'dialogue' = 'dialogue',
): Promise<string> {
  const { getVoiceConfig, getNarrationVoiceConfig } = await import('./languages')
  const pickConfig = voiceSurface === 'dialogue' ? getVoiceConfig : getNarrationVoiceConfig
  const { provider, voice, model, langCode } = pickConfig(language, gender)

  const timeout = AbortSignal.timeout(30_000)
  const combined = signal ? AbortSignal.any([signal, timeout]) : timeout

  const buffer = provider === 'sarvam'
    ? await fetchSarvam(text, voice, model, langCode, combined)
    : await fetchOpenAI(text, voice, model, combined)

  return bufferToBase64(buffer)
}

/** Play a base64 audio string (MP3 or WAV) and return when done.
 *  Pass an audioRef to be able to stop playback early via audioRef.current.pause(). */
export function playB64Audio(
  b64: string,
  audioRef?: { current: HTMLAudioElement | null },
  mimeType = 'audio/mpeg',
): Promise<void> {
  const blob = base64ToBlob(b64, mimeType)
  const url = URL.createObjectURL(blob)
  return new Promise((resolve, reject) => {
    const audio = new Audio(url)
    if (audioRef) audioRef.current = audio
    const cleanup = () => { URL.revokeObjectURL(url); if (audioRef) audioRef.current = null }
    audio.onended = () => { cleanup(); resolve() }
    audio.onerror = () => { cleanup(); reject(new Error('Playback failed')) }
    audio.play().catch(e => { cleanup(); reject(e) })
  })
}

/**
 * Pre-load the next segment's audio: fetches (or reads cache), decodes via
 * Web Audio API, and trims leading silence. Call while the current segment
 * plays so the next one is ready with zero gap.
 */
export async function preloadSegmentAudio(
  text: string,
  cachedAudio: string | null,
  language = 'en',
  isCancelled: () => boolean,
  gender: 'female' | 'male' = 'female',
): Promise<{ audioBuffer: AudioBuffer; freshBase64: string | null } | null> {
  // Narration-only helper (PlayPage) — always the narration voice catalog.
  const { getNarrationVoiceConfig } = await import('./languages')
  const { provider, voice, model, langCode, mimeType } = getNarrationVoiceConfig(language, gender)

  let rawBuffer: ArrayBuffer
  let freshBase64: string | null = null

  if (cachedAudio) {
    const blob = base64ToBlob(cachedAudio, mimeType)
    rawBuffer = await blob.arrayBuffer()
  } else {
    const timeout = AbortSignal.timeout(30_000)
    try {
      if (provider === 'sarvam') {
        rawBuffer = await fetchSarvam(text, voice, model, langCode, timeout)
      } else {
        rawBuffer = await fetchOpenAI(text, voice, model, timeout)
      }
    } catch {
      return null
    }
    if (isCancelled()) return null
    freshBase64 = bufferToBase64(rawBuffer)
  }

  if (isCancelled()) return null

  // Decode and trim leading silence (eliminates the inter-segment gap)
  try {
    const ctx = getAudioCtx()
    const decoded = await ctx.decodeAudioData(rawBuffer.slice(0))
    const audioBuffer = trimLeadingSilence(decoded)
    return { audioBuffer, freshBase64 }
  } catch {
    return null
  }
}

/**
 * Play a pre-decoded AudioBuffer via Web Audio API.
 * Pass stopFnRef so the caller can cancel playback early (e.g. user clicks Next).
 */
export function playPreloadedBuffer(
  audioBuffer: AudioBuffer,
  stopFnRef: { current: (() => void) | null },
  isCancelled: () => boolean,
): Promise<void> {
  const ctx = getAudioCtx()
  return new Promise<void>((resolve) => {
    const source = ctx.createBufferSource()
    source.buffer = audioBuffer
    source.connect(ctx.destination)
    stopFnRef.current = () => { try { source.stop() } catch { /* already stopped */ } }
    source.onended = () => { stopFnRef.current = null; resolve() }
    if (isCancelled()) { stopFnRef.current = null; resolve(); return }
    source.start(0)
  })
}

/** Generate audio (or use cache) and play it. Returns fresh base64 if newly generated,
 *  or null if the audio came from cache or was cancelled. */
export async function speakSegment(
  text: string,
  cachedAudio: string | null,
  audioRef: { current: HTMLAudioElement | null },
  isCancelled: () => boolean,
  language = 'en',
  gender: 'female' | 'male' = 'female',
  // Optional. When provided and it returns true at the moment the
  // audio pauses, this is a DELIBERATE user pause: keep the element +
  // object URL alive (promise stays pending) so audioRef.current.play()
  // resumes the SAME segment. Omitted ⇒ behaviour byte-identical to
  // before (every pause cancels the segment). Callers MUST reset their
  // user-pause flag to false before any real cancel (Next/Back/slide
  // change/stopAudio) and re-dispatch a 'pause' event for the
  // already-paused case so a cancel while user-paused still resolves.
  isUserPause?: () => boolean,
  // Fires the moment playback actually BEGINS — after any storage fetch or
  // live TTS generation inside this function. Callers use it for two things:
  // flipping 'loading' → 'playing' so the spinner covers the real wait, and
  // revealing the matching content in sync with the voice (revealing earlier
  // would jump ahead of the audio by the generation latency on a cache miss).
  onStart?: () => void,
): Promise<string | null> {
  // Narration-only helper (PlayPage / PlayerPage) — always the narration voice catalog.
  const { getNarrationVoiceConfig } = await import('./languages')
  const { provider, voice, model, langCode, mimeType } = getNarrationVoiceConfig(language, gender)

  let audioBlob: Blob
  let freshBase64: string | null = null

  if (cachedAudio) {
    audioBlob = base64ToBlob(cachedAudio, mimeType)
  } else {
    const timeout = AbortSignal.timeout(30_000)
    let buffer: ArrayBuffer

    if (provider === 'sarvam') {
      buffer = await fetchSarvam(text, voice, model, langCode, timeout)
    } else {
      buffer = await fetchOpenAI(text, voice, model, timeout)
    }

    if (isCancelled()) return null

    freshBase64 = bufferToBase64(buffer)
    audioBlob = new Blob([buffer], { type: mimeType })
  }

  if (isCancelled()) return null

  const url = URL.createObjectURL(audioBlob)
  return new Promise((resolve, reject) => {
    const audio = new Audio(url)
    audioRef.current = audio
    // Idempotent teardown — a deliberate cancel may re-fire onpause
    // (synthetic dispatch); double invocation must be a no-op.
    let settled = false
    const cleanup = () => { if (settled) return; settled = true; URL.revokeObjectURL(url); audioRef.current = null }
    audio.onended = () => { if (settled) return; cleanup(); resolve(freshBase64) }
    audio.onerror = () => { if (settled) return; cleanup(); reject(new Error('Audio playback failed')) }
    // Pause handling. A deliberate USER pause must NOT cancel the
    // segment — leave the element + object URL alive (promise stays
    // pending) so audioRef.current.play() resumes the SAME audio; it
    // ends naturally via onended later. ANY other pause (Next/Back/
    // slide-change/stopAudio) keeps the original behaviour: resolve
    // (null) so the narration loop exits cleanly instead of hanging.
    audio.onpause = () => {
      if (settled || audio.ended) return
      if (isUserPause && isUserPause()) return
      cleanup(); resolve(null)
    }
    if (isCancelled()) { cleanup(); resolve(null); return }
    // Fire onStart only once play() RESOLVES — that is the moment audio is
    // genuinely playing, so neither the status flip nor the reveal runs ahead
    // of the sound.
    audio.play().then(() => { if (!settled) onStart?.() }).catch(e => { cleanup(); reject(e) })
  })
}
